import { EuiSelectableOption } from '@elastic/eui';
import { Election } from '@lib/domain/elections';
import {
  GetPvtVdsFilterInput,
  GetPvtVdsSortInput,
  StructureInput,
} from '@lib/domain/stuctures';
import { User } from '@lib/domain/user';
import {
  ELECTION_STORAGE_DATE,
  ELECTION_STORAGE_KEY,
  toDateOnly,
} from '@lib/utils';
import {
  createContext,
  SetStateAction,
  useCallback,
  useEffect,
  useState,
} from 'react';

const readStoredElectionId = (): string | undefined => {
  try {
    return window.localStorage.getItem(ELECTION_STORAGE_KEY) ?? undefined;
  } catch {
    return undefined;
  }
};

const writeStoredElectionId = (id: string) => {
  try {
    window.localStorage.setItem(ELECTION_STORAGE_KEY, id);
  } catch {
    // storage unavailable (private mode / blocked) - selection still works in memory
  }
};

// Reads the saved election start date (YYYY-MM-DD) from localStorage, if any
const readStoredElectionDate = (): string | undefined => {
  try {
    return window.localStorage.getItem(ELECTION_STORAGE_DATE) ?? undefined;
  } catch {
    // storage unavailable (private mode / blocked) - treat as nothing saved
    return undefined;
  }
};

// Saves the selected election's start date (YYYY-MM-DD) to localStorage
const writeStoredElectionDate = (date: string) => {
  try {
    window.localStorage.setItem(ELECTION_STORAGE_DATE, date);
  } catch {
    // storage unavailable (private mode / blocked) - date still works in memory
  }
};

export type PVTContextType = {
  setElection: (election: Election | undefined) => void;
  election: Election | undefined;
  electionId: string | undefined;
  storedElectionId: string | undefined;
  // Selected election's start date as YYYY-MM-DD (no time)
  electionStartDate: string | undefined;
  // Start date saved in localStorage (YYYY-MM-DD), available before elections load
  storedElectionStartDate: string | undefined;
  electionHydrated: boolean;
  setSelectedElection: (selectedElection: EuiSelectableOption[]) => void;
  selectedElection: EuiSelectableOption[];
  setStructure: (structure: StructureInput | null) => void;
  structure: StructureInput | null;
  setPrevStructure: (structure: StructureInput | null) => void;
  prevStructure: StructureInput | null;
  setScore: (score: string) => void;
  setSort: (sort: SetStateAction<GetPvtVdsSortInput>) => void;
  sort: GetPvtVdsSortInput;
  score: string;
  offset: number;
  setOffset: any;
  tablePage: number;
  setTablePage: any;
  tablePageSize: number;
  setTablePageSize: any;
  setFilter: (filter: SetStateAction<Partial<GetPvtVdsFilterInput>>) => void;
  filter: Partial<GetPvtVdsFilterInput>;
  setPerson: (person: User) => void;
  person: User;
  restRefresh: any;
  setRestRefresh: any;
};
export const PVTContext = createContext<Partial<PVTContextType>>({});

const PVTProvider = ({ children }: any) => {
  const [election, setElectionState] = useState<Election | undefined>();
  const [storedElectionId, setStoredElectionId] = useState<string>();
  // Start date (YYYY-MM-DD) of the selected election, mirrored in localStorage
  const [storedElectionStartDate, setStoredElectionStartDate] =
    useState<string>();
  const [electionHydrated, setElectionHydrated] = useState(false);

  useEffect(() => {
    setStoredElectionId(readStoredElectionId());
    // load the saved start date alongside the saved election id
    setStoredElectionStartDate(readStoredElectionDate());
    setElectionHydrated(true);

    // keep other open tabs in sync when the election changes
    const onStorage = (e: StorageEvent) => {
      if (e.key === ELECTION_STORAGE_KEY && e.newValue) {
        setStoredElectionId(e.newValue);
      }
      // also keep the start date in sync across tabs
      if (e.key === ELECTION_STORAGE_DATE && e.newValue) {
        setStoredElectionStartDate(e.newValue);
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const setElection = useCallback((next: Election | undefined) => {
    setElectionState(next);
    if (next?.id) {
      setStoredElectionId(next.id);
      writeStoredElectionId(next.id);
    }
    // save the start date as YYYY-MM-DD whenever an election is picked
    const startDate = toDateOnly(next?.endDate);
    if (startDate) {
      setStoredElectionStartDate(startDate);
      writeStoredElectionDate(startDate);
    }
  }, []);

  const [sort, setSort] = useState<GetPvtVdsSortInput>({
    field: 'score',
    direction: 'desc',
  });
  const [structure, setStructure] = useState<StructureInput | null>(null);
  const [prevStructure, setPrevStructure] = useState<StructureInput | null>(
    null
  );
  const [score, setScore] = useState<string>('scoreGE10');
  const [selectedElection, setSelectedElection] =
    useState<EuiSelectableOption[]>();
  const [offset, setOffset] = useState<number>(0);
  const [tablePage, setTablePage] = useState<number>(0);
  const [tablePageSize, setTablePageSize] = useState<number>(10);
  const [filter, setFilter] = useState<GetPvtVdsFilterInput>();
  const [person, setPerson] = useState<User>();
  const [restRefresh, setRestRefresh] = useState<any>();

  return (
    <PVTContext.Provider
      value={{
        election,
        electionId: election?.id,
        storedElectionId,
        // date-only version of the selected election's startDate
        electionStartDate: toDateOnly(election?.startDate),
        storedElectionStartDate,
        electionHydrated,
        selectedElection,
        structure,
        score,
        prevStructure,
        setElection,
        setSelectedElection,
        setStructure,
        setScore,
        setPrevStructure,
        offset,
        setOffset,
        tablePage,
        setTablePage,
        tablePageSize,
        setTablePageSize,
        setSort,
        sort,
        setFilter,
        filter,
        setPerson,
        person,
        restRefresh,
        setRestRefresh,
      }}>
      {children}
    </PVTContext.Provider>
  );
};

export default PVTProvider;
